import { DataType, Form, FormOptions, Section, SelectOption } from '@globaleris/ui.library'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core'
import MuiDialogTitle from '@material-ui/core/DialogTitle'
import { Description } from '@material-ui/icons'
import { DropzoneArea } from 'material-ui-dropzone'
import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AttachedDocumentFile } from '../../../pages/procedures/procedures.types'
import { getCustomerDomain } from '../../../redux'
import { styles } from './attachFileDialog.styles'
import { AllowedFileTypes } from '../../../config/allowedFileTypes'

type AttachFileDialogProps = {
  open?: boolean
  customTitle?: string
  documentTypes: SelectOption[]
  onClose: () => void
  onSave: (document: AttachedDocumentFile) => void
  onSaveAndNew: (document: AttachedDocumentFile) => void
  prolifeFormConfig?: boolean
  ondownloadTemplate?: () => void
}

export type AttachFileDialogRef = {
  // Reset states to initial values. Sadly, it's not possible to reset files on dropdown without unmounting.
  reset: () => void
}

export const AttachFileDialog = forwardRef<AttachFileDialogRef, AttachFileDialogProps>(
  (
    {
      open = false,
      documentTypes,
      onClose,
      onSave,
      onSaveAndNew,
      prolifeFormConfig = false,
      ondownloadTemplate,
      customTitle
    },
    ref
  ) => {
    const [t] = useTranslation(['procedures', 'globalerisLibrary', 'documents', 'profiles'])

    const currentTheme = useTheme()
    const useStyles = makeStyles(styles(currentTheme))
    const classes = useStyles()

    const [formIsLoading, setFormIsLoading] = useState<boolean>(false)
    const [file, setFile] = useState<File[] | undefined>(undefined)

    const [documentData, setDocumentData] = useState<AttachedDocumentFile>(
      {} as AttachedDocumentFile
    )

    useImperativeHandle(ref, () => ({
      reset: () => {
        // Reset function
        setFormIsLoading(false)
        setFile(undefined)
        setDocumentData({} as AttachedDocumentFile)
      }
    }))

    // Functions Starts
    // Functions Ends

    // Handlers Starts

    const handleSaveDisabled = () => {
      if (prolifeFormConfig) {
        return !file || file?.length === 0 || !documentData.type
      } else {
        return !file || file?.length === 0 || !documentData.type || !documentData.description
      }
    }

    const handleCancel = () => {
      onClose()
    }

    const handleSaveAndNew = () => {
      if (file && file.length > 0) {
        onSaveAndNew({ ...documentData, content: file[0] })
      }
    }

    const handleSave = () => {
      if (file && file.length > 0) {
        onSave({ ...documentData, content: file[0] })
      }
    }

    // Handlers Ends

    // Hooks Starts
    // Hooks Ends

    const options: FormOptions = {
      elevation: 0,
      columns: 4,
      analytics: {
        customer: getCustomerDomain(),
        view: t('documents:forms.add.analytics')
      }
    }

    const formAddSection: Section[] = [
      {
        inputs: [
          {
            name: 'type',
            label: t('documents:forms.add.inputs.type'),
            type: DataType.SELECT,
            selectOptions: documentTypes,
            required: true,
            inputSize: prolifeFormConfig ? 12 : 4,
            defaultValue: documentTypes.length === 1 ? documentTypes[0].value : undefined,
            onChange: value => {
              setDocumentData({ ...documentData, type: value } as AttachedDocumentFile)
            }
          },
          {
            name: 'description',
            label: t('documents:forms.add.inputs.description'),
            type: DataType.TEXT,
            required: !prolifeFormConfig,
            inputSize: 4,
            display: !prolifeFormConfig,
            onChange: value => {
              setDocumentData({ ...documentData, description: value } as AttachedDocumentFile)
            }
          },
          {
            name: 'comments',
            label: t('documents:forms.add.inputs.comments'),
            type: DataType.TEXT,
            inputSize: 4,
            display: !prolifeFormConfig,
            onChange: value => {
              setDocumentData({ ...documentData, comments: value } as AttachedDocumentFile)
            }
          }
        ]
      }
    ]

    const DialogTitle = ({ children, ...other }) => {
      return (
        <MuiDialogTitle disableTypography className={classes.dialogTitle} {...other}>
          <Typography variant="h5" color="secondary">
            {children}
          </Typography>
          {prolifeFormConfig && (
            <Tooltip title={t('profiles:components.DialogTitle.tooltips.downloadTemplate') || ''}>
              <IconButton
                aria-label="download"
                color="secondary"
                className={classes.templateButton}
                onClick={ondownloadTemplate}
              >
                <Description />
              </IconButton>
            </Tooltip>
          )}
        </MuiDialogTitle>
      )
    }

    return (
      <Dialog
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
        onClose={() => {}}
        open={open}
        className={classes.root}
      >
        <DialogContent>
          <Form
            titleCustom={
              <DialogTitle className={classes.formCustomTitle}>
                {customTitle ? customTitle : t('procedures:components.attachFileDialog.title')}
              </DialogTitle>
            }
            sections={formAddSection}
            data={documentData}
            options={options}
          />
          <Box marginX={2}>
            <DropzoneArea
              dropzoneClass={classes.dropZone}
              previewGridProps={{ container: { spacing: 1, direction: 'row' } }}
              previewChipProps={{ classes: { root: classes.previewChip } }}
              showAlerts={false}
              showPreviews={true}
              showPreviewsInDropzone={false}
              useChipsForPreview
              clearOnUnmount={true}
              previewText={t('procedures:components.attachFileDialog.upload.selectedFiles')}
              dropzoneText={t('procedures:components.attachFileDialog.upload.dropText', {
                maxCount: 1
              })}
              filesLimit={1}
              // acceptedFiles={['.xlsx', '.xlsm', '.pdf']}
              onChange={file => {
                setFile(file)
              }}
              initialFiles={undefined}
              maxFileSize={AllowedFileTypes.documents.maxSizeMB * 1024 * 1024}
            />
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActionsRoot}>
          <Button size="small" onClick={handleCancel}>
            {t('procedures:components.attachFileDialog.mainActions.cancel')}
          </Button>
          <Button
            size="small"
            onClick={handleSaveAndNew}
            color="primary"
            disabled={handleSaveDisabled()}
          >
            {t('procedures:components.attachFileDialog.mainActions.saveAndNew')}
          </Button>
          <Button size="small" onClick={handleSave} color="primary" disabled={handleSaveDisabled()}>
            {t('procedures:components.attachFileDialog.mainActions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)
